const API_BASE_URL = "https://api.skylls.uz/api/v1"

// Token management
export const getAccessToken = () => localStorage.getItem("access_token")
export const getRefreshToken = () => localStorage.getItem("refresh_token")
export const setTokens = (access: string, refresh: string) => {
  localStorage.setItem("access_token", access)
  localStorage.setItem("refresh_token", refresh)
}
export const clearTokens = () => {
  localStorage.removeItem("access_token")
  localStorage.removeItem("refresh_token")
}

// Enhanced fetch with automatic retry and auth
class APIClient {
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value?: unknown) => void
    reject: (reason?: unknown) => void
  }> = []

  private processQueue(error: Error | null, token: string | null = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    this.failedQueue = []
  }

  async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    const token = getAccessToken()

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    }

    // Only set Content-Type if not already provided
    // (FormData needs the browser to set it automatically with boundary)
    if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json"
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // Handle 401 - Unauthorized
      if (response.status === 401) {
        if (this.isRefreshing) {
          // Wait for token refresh
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject })
          }).then(() => this.request(endpoint, options))
        }

        this.isRefreshing = true
        const refreshToken = getRefreshToken()

        if (!refreshToken) {
          clearTokens()
          window.location.href = "/login"
          throw new Error("No refresh token available")
        }

        try {
          // Attempt to refresh token
          const refreshResponse = await fetch(
            `${API_BASE_URL}/users/token-refresh/`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refresh: refreshToken }),
            }
          )

          if (!refreshResponse.ok) {
            throw new Error("Token refresh failed")
          }

          const { access } = await refreshResponse.json()
          setTokens(access, refreshToken)
          this.processQueue(null, access)

          // Retry original request
          return this.request(endpoint, options)
        } catch (error) {
          this.processQueue(error as Error, null)
          clearTokens()
          window.location.href = "/login"
          throw error
        } finally {
          this.isRefreshing = false
        }
      }

      // Handle non-OK responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw {
          status: response.status,
          message: response.statusText,
          data: errorData,
          response: errorData,
        }
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return undefined as any
      }

      return await response.json()
    } catch (error) {
      throw error
    }
  }

  async get<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" })
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data instanceof FormData ? data : JSON.stringify(data),
    })
  }

  async put<T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data instanceof FormData ? data : JSON.stringify(data),
    })
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data instanceof FormData ? data : JSON.stringify(data),
    })
  }

  async delete<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" })
  }
}

export const apiClient = new APIClient()
export default apiClient
